import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteIcon from "@mui/icons-material/Delete";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import WorkIcon from "@mui/icons-material/Work";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import { alpha, styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import Loader from "_shared/_views/loading/loader";
import LoadingButton from "_shared/_views/loading/loading-button";
import { skipToken } from "@reduxjs/toolkit/query";
import { enqueueSnackbar } from "notistack";
import useSessionUser from "permissions/session/use-session-user";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import { useSession } from "users/__session/session-context";
import { startDraftProposalFromTeamTemplate } from "work/proposals/_views/drafts/start-draft-proposal-from-template";
import {
  useArchiveTeamTemplateMutation,
  useDeleteTeamTemplateMutation,
  useGetTeamTemplateByIdQuery,
  useUnarchiveTeamTemplateMutation,
} from "work/team-templates/_api/team-template-api";
import {
  getErrorLoadingTeamTemplateById,
  getIsLoadingTeamTemplateById,
  getTeamTemplateById,
} from "work/team-templates/_store/team-templates-redux-slice";
import TeamTemplateForm from "work/team-templates/_views/team-template-form";

const PageContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  height: "100%",
  overflow: "hidden",
}));

const Main = styled("main", {
  shouldForwardProp: (prop) => prop !== "dimmed",
})<{
  dimmed?: boolean;
}>(({ dimmed, theme }) => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minWidth: 0,
  overflow: "hidden",
  position: "relative",
  "&::after": {
    backgroundColor: alpha(theme.palette.background.default, 0.38),
    content: '""',
    inset: 0,
    opacity: dimmed ? 1 : 0,
    pointerEvents: "none",
    position: "absolute",
    transition: theme.transitions.create("opacity", {
      duration: dimmed ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
      easing: dimmed ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
    }),
    zIndex: theme.zIndex.drawer - 1,
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  padding: theme.spacing(3),
  paddingBottom: theme.spacing(1),
}));

const TitleContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
}));

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(2),
}));

const SaveButtonContainer = styled("span")(() => ({
  display: "inline-flex",
}));

const Content = styled("section")(({ theme }) => ({
  flex: 1,
  overflow: "auto",
  padding: theme.spacing(2, 3, 3),
}));

const LoaderContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  height: "100%",
  justifyContent: "center",
}));

const SELECTION_DRAWER_WIDTH = "min(500px, 100vw)";
const DRAWER_LAYOUT_TRANSITION_TIMEOUT_MS = 250;

type TeamTemplateAction = "create" | "edit" | "view";
const TeamTemplateActions = new Set<TeamTemplateAction>(["create", "edit", "view"]);

export default function TeamTemplatePage() {
  const { action: actionParam, id: idParam } = useParams();
  const pageAction: TeamTemplateAction = (actionParam as TeamTemplateAction) ?? "create";
  const templateId = React.useMemo(() => (idParam ? new Guid(idParam) : undefined), [idParam]);
  const location = useLocation();

  if (!TeamTemplateActions.has(pageAction)) {
    throw new Response("Not Found", { status: 404 });
  }

  if (pageAction !== "create" && !templateId) {
    throw new Response("Not Found", { status: 404 });
  }

  const navigate = useNavigate();
  const session = useSession();
  const confirm = useConfirmDialog();
  const dispatch = useAttorneyHubDispatch();
  const [isFormDirty, setIsFormDirty] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isArchiving, setIsArchiving] = React.useState(false);
  const [isSelectionDrawerOpen, setIsSelectionDrawerOpen] = React.useState(false);
  const [saveButtonContainer, setSaveButtonContainer] = React.useState<HTMLSpanElement | null>(null);
  const [deleteTeamTemplate] = useDeleteTeamTemplateMutation();
  const [archiveTeamTemplate] = useArchiveTeamTemplateMutation();
  const [unarchiveTeamTemplate] = useUnarchiveTeamTemplateMutation();
  const teamTemplateQuery = useGetTeamTemplateByIdQuery(
    pageAction !== "create" && templateId ? { templateId: templateId.value } : skipToken,
  );

  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionUser = useSessionUser();
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);
  const teamTemplate = useSelector(getTeamTemplateById(templateId));
  const isLoadingTeamTemplate =
    useSelector(getIsLoadingTeamTemplateById(templateId)) || teamTemplateQuery.isLoading || teamTemplateQuery.isFetching;
  const errorLoadingTeamTemplate = useSelector(getErrorLoadingTeamTemplateById(templateId)) || teamTemplateQuery.isError;
  const isTeamTemplateMissing = teamTemplateQuery.data === null || errorLoadingTeamTemplate;
  const disableEditing = pageAction === "view";
  const isArchivedTemplateRoute = location.pathname.startsWith("/teams/archived/");
  const isTemplateArchived = isArchivedTemplateRoute || Boolean(teamTemplate?.archived);
  const listPath = isArchivedTemplateRoute ? "/teams/archived" : "/teams/templates";

  React.useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  React.useEffect(() => {
    if (pageAction === "create" || !templateId || teamTemplate || isLoadingTeamTemplate || isDeleting) return;

    if (!isTeamTemplateMissing) return;
    enqueueSnackbar("Team template not found", { variant: "warning" });
    navigate(listPath, { replace: true });
  }, [
    isDeleting,
    isLoadingTeamTemplate,
    isTeamTemplateMissing,
    listPath,
    navigate,
    pageAction,
    teamTemplate,
    templateId,
  ]);

  React.useEffect(() => {
    globalThis.dispatchEvent(new Event("resize"));

    const timeoutId = globalThis.setTimeout(() => {
      globalThis.dispatchEvent(new Event("resize"));
    }, DRAWER_LAYOUT_TRANSITION_TIMEOUT_MS);

    return () => globalThis.clearTimeout(timeoutId);
  }, [isSelectionDrawerOpen]);

  const handleNavigateBack = React.useCallback(async () => {
    if (isFormDirty) {
      const response = await confirm({
        title: "Discard changes?",
        message: "Are you sure you want to discard changes?",
        okButtonText: "Discard",
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;
    }

    navigate(listPath, { replace: true });
  }, [confirm, isFormDirty, listPath, navigate]);

  async function handleDeleteClicked() {
    if (!templateId) return;

    try {
      const response = await confirm({
        title: "Delete Team Template?",
        message: "This cannot be undone.",
        okButtonText: "Delete",
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;

      setIsDeleting(true);
      await deleteTeamTemplate({ templateId: templateId.value }).unwrap();
      enqueueSnackbar("Deleted vendor-team template", { variant: "success" });
      navigate("/teams/templates", { replace: true });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to delete vendor-team template. Please try again", { variant: "error" });
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleArchiveClicked() {
    if (!templateId || !teamTemplate) return;

    const shouldArchive = !isTemplateArchived;
    const action = shouldArchive ? "Archive" : "Unarchive";

    try {
      const response = await confirm({
        title: `${action} Team Template?`,
        message: `${action} team template?`,
        okButtonText: action,
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;

      setIsArchiving(true);
      if (shouldArchive) {
        await archiveTeamTemplate({ templateId: templateId.value }).unwrap();
      } else {
        await unarchiveTeamTemplate({ templateId: templateId.value }).unwrap();
      }
      enqueueSnackbar(`${action}d vendor-team template`, { variant: "success" });
      navigate(shouldArchive ? "/teams/archived" : "/teams/templates", { replace: true });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Failed to ${action.toLowerCase()} vendor-team template. Please try again`, { variant: "error" });
    } finally {
      setIsArchiving(false);
    }
  }

  const handleCreateProposalClicked = React.useCallback(() => {
    if (!teamTemplate) return;
    startDraftProposalFromTeamTemplate({
      dispatch,
      navigate,
      session,
      template: teamTemplate,
    });
  }, [dispatch, navigate, session, teamTemplate]);

  if (!sessionPermissions) {
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );
  }
  if (!sessionPermissions.includesAccessToTeams) return <NotAuthorized />;
  // Authoring a team template is an entity-wide act — see `User.canCreateTeamTemplates`.
  // Refusing here rather than at Save is the point: every route into a new template (the Teams page's
  // Create Template button, a pasted URL) lands on this component, so this one gate stops anyone
  // filling in a whole template the API was always going to answer 403 to (issue #4948).
  if (pageAction === "create" && !sessionUser?.canCreateTeamTemplates()) {
    return (
      <NotAuthorized message="You do not have permission to create a team template. Ask an administrator at your firm for entity-wide team access." />
    );
  }

  let pageTitle = "Edit Template";
  if (pageAction === "create") {
    pageTitle = "Create";
  } else if (pageAction === "view") {
    pageTitle = "View";
  }

  return (
    <PageContainer>
      <Main dimmed={isSelectionDrawerOpen}>
        <HeaderContainer>
          <TitleContainer>
            <Chip
              icon={<ArrowBackIcon />}
              size="medium"
              label="Back"
              onClick={handleNavigateBack}
              color="primary"
              sx={{ mr: 2 }}
            />
            <Typography variant="h4">
              <span style={{ color: "gray", marginRight: "8px" }}>Teams &gt;</span>
              {pageTitle}
            </Typography>
          </TitleContainer>
          <ActionButtonsContainer>
            {pageAction === "edit" && !isTemplateArchived && (
              <LoadingButton
                variant="outlined"
                size="large"
                color="primary"
                loading={false}
                disabled={isLoadingTeamTemplate || !teamTemplate}
                startIcon={<WorkIcon />}
                onClick={handleCreateProposalClicked}
              >
                Create Proposal
              </LoadingButton>
            )}
            {pageAction === "edit" && (
              <LoadingButton
                variant="outlined"
                size="large"
                color="error"
                loading={isDeleting}
                disabled={isDeleting || isArchiving || isLoadingTeamTemplate}
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClicked}
              >
                Delete
              </LoadingButton>
            )}
            {pageAction === "edit" || isTemplateArchived ? (
              <LoadingButton
                variant="outlined"
                size="large"
                color="inherit"
                loading={isArchiving}
                disabled={isDeleting || isArchiving || isLoadingTeamTemplate || !teamTemplate}
                startIcon={isTemplateArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
                onClick={handleArchiveClicked}
              >
                {isTemplateArchived ? "Unarchive" : "Archive"}
              </LoadingButton>
            ) : null}
            <SaveButtonContainer ref={setSaveButtonContainer} />
          </ActionButtonsContainer>
        </HeaderContainer>
        <Content>
          {pageAction === "edit" && isLoadingTeamTemplate && !teamTemplate ? (
            <LoaderContainer>
              <Loader fontSize="20px" />
            </LoaderContainer>
          ) : (
            <TeamTemplateForm
              templateId={templateId}
              disableEditing={disableEditing}
              navigate={navigate}
              onFormDirty={(dirty) => setIsFormDirty(dirty === true)}
              saveButtonContainer={saveButtonContainer}
              saveButtonLabel="Save"
              onSelectionDrawerOpenChange={setIsSelectionDrawerOpen}
              onSave={(template) => {
                setIsFormDirty(false);
                if (pageAction === "create" && template.id) {
                  navigate(`/teams/templates/edit/${template.id.value}`, { replace: true });
                }
              }}
            />
          )}
        </Content>
      </Main>
      <div style={{ width: isSelectionDrawerOpen ? SELECTION_DRAWER_WIDTH : 0 }} />
    </PageContainer>
  );
}
